import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';

import { translate as t } from '../../lib/I18n';
import AutocompleteOption from './AutocompleteOption';
import Icon from '../general/Icon';

const KEY_UP = 38;
const KEY_DOWN = 40;
const KEY_ESCAPE = 27;
const KEY_ENTER = 13;
const KEY_TAB = 9;

class Autocomplete extends React.PureComponent {

    static propTypes = {
        name: PropTypes.string.isRequired,
        defaultValue: PropTypes.any,
        defaultInputValue: PropTypes.any,
        placeholder: PropTypes.string,
        floatyPlaceholder: PropTypes.bool,
        className: PropTypes.string,
        results: PropTypes.array,
        searchValue: PropTypes.string,
        onSearch: PropTypes.func,
        onSelect: PropTypes.func,
        onClear: PropTypes.func,
        optionElement: PropTypes.func,
        debounce: PropTypes.bool,
        required: PropTypes.bool,
        resultsFilter: PropTypes.func,
        label: PropTypes.node,
        resultsLabel: PropTypes.string,
        overlayResults: PropTypes.bool,
        disabled: PropTypes.bool,
        focused: PropTypes.bool,
        title: PropTypes.string,
        isInvalid: PropTypes.bool,
        errorMessage: PropTypes.string,
    };

    static defaultProps = {
        name: '',
        defaultInputValue: '',
        defaultValue: '',
        defaultLabel: '',
        placeholder: null,
        floatyPlaceholder: false,
        onSearch: _.noop,
        onSelect: _.noop,
        onClear: _.noop,
        className: '',
        results: [],
        searchValue: '',
        optionElement: null,
        debounce: true,
        required: false,
        resultsFilter: null,
        label: null,
        resultsLabel: null,
        overlayResults: false,
        disabled: false,
        focused: false,
        title: null,
        isInvalid: false,
        errorMessage: null,
    };

    getDefaultState ({ open = false } = {}) {
        return {
            open,
            value: this.props.defaultValue,
            inputValue: this.props.defaultInputValue,
            searching: false,
            highlighted: -1
        };
    }

    constructor (props) {
        super(props);
        this.state = this.getDefaultState();
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        let newState;

        if (newProps.defaultInputValue !== this.props.defaultInputValue ||
                newProps.defaultValue !== this.props.defaultValue) {

            newState = this.getDefaultState({ open: this.state.open });
            newState.value = newProps.defaultValue;
            newState.inputValue = newProps.defaultInputValue;
        }

        if (this.state.searching
                && newProps.results !== this.props.results
                && newProps.searchValue === this.state.inputValue) {

            newState = {
                searching: false,
                open: true
            };
        }

        if (newState) { this.setState(newState); }
    }

    UNSAFE_componentWillMount () {
        if (this.props.debounce) {
            this.onSearch = _.debounce(this.props.onSearch, 200);
        } else {
            this.onSearch = this.props.onSearch;
        }
    }

    componentDidUpdate () {
        if (this.props.focused) {
            this.refs.input.focus();
        }
    }

    handleChange = (event) => {
        let value = event.target.value;
        if (value === this.state.inputValue) { return; }

        if (value.length >= 3) {
            this.setState({
                inputValue: value,
                searching: true,
            }, () => {
                if (this.props.debounce) { this.onSearch.cancel(); }
                this.onSearch(value);
            });
        } else {
            this.setState({
                inputValue: value,
                searching: false,
                results: []
            });

            if (!value) { this.props.onClear(value); }
        }
    };

    togglePrevious () {
        let highlighted = this.state.highlighted;

        if (this.getResults().length && !this.state.searching) {
            if (highlighted !== 0) { highlighted = this.state.highlighted - 1; }

            this.setState({
                open: true,
                highlighted: highlighted
            });
        }
    }

    toggleNext () {
        let highlighted = this.state.highlighted;

        if (this.getResults().length && !this.state.searching) {
            if (highlighted !== (this.getResults().length - 1)) {
                highlighted = this.state.highlighted + 1;
            }

            this.setState({
                open: true,
                highlighted: highlighted
            });
        }
    }

    handleKeyDown = (event) => {
        let key = event.keyCode;

        let handlers = {
            [ KEY_UP ]: this.togglePrevious,
            [ KEY_DOWN ]: this.toggleNext,
            [ KEY_ENTER ]: this.selectHighlighted,
            [ KEY_TAB ]: this.selectHighlighted,
            [ KEY_ESCAPE ]: this.hideResults
        };


        if (handlers[key]) {
            handlers[key].call(this);
            if (key !== KEY_TAB) { event.preventDefault(); }
        }
    };

    hideResults () {
        this.setState({
            open: false,
            searching: false,
            highlighted: -1
        });
    }

    selectHighlighted () {
        if (this.getResults().length) {
            let highlighted = this.state.highlighted >= 0 ? this.state.highlighted : 0;
            let result = this.getResults()[highlighted];
            this.selectValue(result.value, result.label);
        }
    }

    _handleOptionSelect (result) {
        return (e) => {
            this.selectValue(result.value, result.label);
            e.preventDefault();
        };
    }

    _handleClear = () => {
        this.refs.input.focus();
        this.setState({ open: true }, this.props.onClear);
    };

    selectValue (value, label) {
        const newState = this.getDefaultState();
        this.setState(Object.assign(newState, { value, label }), () => this.props.onSelect(value));
    }

    getOptionId(index) {
        return `autocomplete-${this.props.name}-option-${index}`;
    }

    getActiveDescendentId() {
        const {highlighted} = this.state;
        return highlighted === -1 ? null : this.getOptionId(highlighted);
    }

    renderResults (results = []) {
        if (!this.state.open) {
            return null;
        }

        if (results.length) {
            return results.map((result, index) => {
                const isActive = index === this.state.highlighted;
                return (
                    <li
                        key={result.value}
                        id={this.getOptionId(index)}
                        role="option"
                        aria-selected={isActive}
                        onClick={ this._handleOptionSelect(result) }
                    >
                        <AutocompleteOption value={ result.value }
                                    search= { this.state.inputValue }
                                    label={ result.label }
                                    data={ result.data }
                                    highlighted={ isActive }
                                    optionElement={ this.props.optionElement }/>
                    </li>
                );
            });
        }

        if (!this.state.searching) {
            return this.renderNoResults();
        }

        return null;
    }

    renderNoResults () {
        return <div className="autocomplete__noResults" role="status">No results found</div>;
    }

    renderSearchIcon () {
        if (!this.state.searching) {
            return null;
        }

        return <i className="autocomplete__spinner otrl otrl-spinner otrl-spin" />;
    }

    renderClearButton () {
        if (!this.props.defaultValue || this.state.searching) {
            return null;
        }

        const buttonProps = {
            type: 'button',
            'aria-label': t('ariaLabel.clearContent'),
            onClick: this._handleClear,
            className: 'autocomplete__clear',
            tabIndex: '0'
        };

        return (
            <button { ...buttonProps }>
                <Icon icon="clear"/>
                <span className="sr-only">
                    { t('booking.journey.clear') }
                </span>
            </button>
        );
    }

    renderPlaceholder () {
        if (this.state.inputValue && this.props.placeholder && this.props.floatyPlaceholder) {
            return <span className="placeholder text-sm">{ this.props.placeholder }</span>;
        }
        return null;
    }

    handleBlur = () => {
        if (this.state.open && this.state.inputValue) {
            this.selectHighlighted();
        } else {
            this.hideResults();
        }
    };

    preventBlur (e) {
        e.preventDefault();
        e.stopPropagation();
    }

    handleFocus = () => {
        if (!this.state.value && this.getResults().length) {
            this.setState({ open: true });
        }
    };

    getResults() {
        const { resultsFilter, results } = this.props;
        const { inputValue = '' } = this.state;

        return _.uniqBy(
            resultsFilter ? results.filter(resultsFilter.bind(null, inputValue)) : results,
            'value'
        );
    }

    render () {
        const {
            disabled,
            label,
            resultsLabel,
            name,
            title,
            overlayResults,
            placeholder,
            required,
            isInvalid,
            errorMessage,
        } = this.props;
        const { open, inputValue, searching } = this.state;
        const resultsList = this.getResults();
        const containerClasses = classnames('autocomplete__container', {
            'input-field--autocomplete': overlayResults
        });
        const classes = classnames(this.props.className, {
            'autocomplete': true,
            'autocomplete--open': open
        });

        const showResultsList = open && !searching;
        const resultsId = `autocomplete-${name}-results`;
        return (
            <div
                className={ containerClasses }
            >
                <label>{ label }
                    <input
                        type="text"
                        name={ name }
                        className={ classes }
                        ref="input"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck="false"
                        title={ title || name }
                        value={ inputValue || '' }
                        placeholder={ placeholder }
                        onChange={ this.handleChange }
                        onKeyDown={ this.handleKeyDown }
                        onFocus={ this.handleFocus }
                        onBlur={ this.handleBlur }
                        disabled={ disabled }
                        required={ required }
                        role="combobox"
                        aria-haspopup="listbox"
                        aria-owns={ open ? resultsId : null }
                        aria-expanded={ open }
                        aria-autocomplete="list"
                        aria-controls={ open ? resultsId : null }
                        aria-activedescendant={ this.getActiveDescendentId() }
                        aria-invalid={ isInvalid ? "true" : "false"}
                        aria-errormessage={ errorMessage }
                    />
                </label>
                { showResultsList && resultsList.length ? (
                    <div className="sr-only" role="status">
                        {t('autocomplete.resultCount', { count: resultsList.length })}
                    </div>
                ) : null }
                { required && <Icon icon="mandatory" /> }
                { !disabled && this.renderClearButton() }
                { this.renderSearchIcon() }
                { this.renderPlaceholder() }
                { showResultsList && (
                    <ul
                        id={ resultsId }
                        ref={ el => { this.resultsListElement = el; } }
                        className="autocomplete__results"
                        onMouseDown={ this.preventBlur }
                        onClick={ this.preventBlur }
                        aria-label={ resultsLabel }
                        role="listbox"
                    >
                        { this.renderResults(resultsList) }
                    </ul>
                ) }
            </div>
        );
    }

}

export default Autocomplete;



// WEBPACK FOOTER //
// ./src/components/form/Autocomplete.js